import { useAuth } from "@clerk/clerk-react";
import { useCallback, useMemo } from "react";
import { CLERK_AVAILABLE } from "./auth";

// Type definition for auth hook return value
export interface AuthHook {
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
}

// Stable demo mode getToken function (never changes)
const demoGetToken = async (): Promise<string | null> => null;

/**
 * Auth hook that works with or without Clerk
 * - When Clerk is configured: uses real auth
 * - When Clerk is NOT configured: demo mode (always signed in)
 */
export function useAuthConditional(): AuthHook {
  // If Clerk is available, use the real auth hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const clerkAuth = CLERK_AVAILABLE ? useAuth() : null;

  // Memoize getToken to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getToken = useCallback(async (): Promise<string | null> => {
    if (CLERK_AVAILABLE && clerkAuth?.getToken) {
      return clerkAuth.getToken();
    }
    return null;
  }, [clerkAuth?.getToken]);

  // Memoize the return object to maintain referential stability
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(() => {
    if (CLERK_AVAILABLE && clerkAuth) {
      return {
        isSignedIn: clerkAuth.isSignedIn ?? false,
        getToken,
      };
    }

    // Demo mode: always signed in with no token
    return {
      isSignedIn: true,
      getToken: demoGetToken,
    };
  }, [clerkAuth?.isSignedIn, getToken, clerkAuth]);
}

// Export for reference
export { CLERK_AVAILABLE };
